import { Button, Collapsible } from '@shopify/polaris';
import { useLayoutEffect, useRef, useState } from 'react';
import PdfPane from '../../../shared/components/pdf/PdfPane';
import { useIsDesktop } from '../hooks/useWorkspaceHotkeys';

/*
  Two-column workspace: PDF left (sticky, full height), scrollable details right with a sticky action bar.
  Below 48em the PDF collapses behind a button and everything stacks.
*/
export default function ApprovalWorkspace({ resource, s3Keys, selectedKey, onSelectKey, pdfCache, children, footer, pdfRef }) {
  const isDesktop = useIsDesktop();
  const [pdfOpen, setPdfOpen] = useState(false);
  const rootRef = useRef(null);
  const [height, setHeight] = useState('calc(100vh - 150px)');

  // fill the viewport below whatever sits above the workspace (page header, tabs, progress)
  useLayoutEffect(() => {
    if (!isDesktop) return undefined;
    const measure = () => {
      if (!rootRef.current) return;
      const top = rootRef.current.getBoundingClientRect().top + window.scrollY;
      setHeight(`calc(100vh - ${Math.round(top)}px - 16px)`);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isDesktop]);
  const pdf = <PdfPane resource={resource} keys={s3Keys} selectedKey={selectedKey} onSelectKey={onSelectKey} cache={pdfCache} height="100%" />;

  if (!isDesktop) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Button onClick={() => setPdfOpen((v) => !v)} disclosure={pdfOpen ? 'up' : 'down'} fullWidth>
          {pdfOpen ? 'Hide PDF' : 'Show PDF'}
        </Button>
        <Collapsible open={pdfOpen} id="approval-pdf">
          <div style={{ height: '60vh' }} ref={pdfRef}>
            {pdf}
          </div>
        </Collapsible>
        <div>{children}</div>
        <div style={{ position: 'sticky', bottom: 0, background: '#f6f6f7', padding: '8px 0', zIndex: 5 }}>{footer}</div>
      </div>
    );
  }

  return (
    <div ref={rootRef} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 55fr) minmax(0, 45fr)', gap: 16, height, minHeight: 520 }}>
      <div ref={pdfRef} tabIndex={-1} style={{ height: '100%', minHeight: 0, background: '#f6f6f7', borderRadius: 8, padding: 4, outline: 'none' }}>
        {pdf}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, paddingRight: 4 }}>{children}</div>
        <div style={{ borderTop: '1px solid #e1e3e5', paddingTop: 10, background: 'white' }}>{footer}</div>
      </div>
    </div>
  );
}
